import { Box, CircularProgress, IconButton, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { MdClose, MdPictureAsPdf, MdCloudUpload } from 'react-icons/md'
import { toast } from 'react-toastify'
import { axios } from 'src/axios'

export interface UploadedFile {
  fileId: number
  fileName: string
  fileUrl?: string
  viewToken?: string
}

interface UploadStepProps {
  uploadedFiles: UploadedFile[]
  onFilesChange: (files: UploadedFile[]) => void
}

export const UploadStep: React.FC<UploadStepProps> = ({ uploadedFiles, onFilesChange }) => {
  const [isUploading, setIsUploading] = useState(false)

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setIsUploading(true)
      const newFiles: UploadedFile[] = [...uploadedFiles]

      for (const file of acceptedFiles) {
        const formData = new FormData()
        formData.append('file', file)
        try {
          const response = await axios.post('files/upload-file', formData)
          const uploaded = response.data
          newFiles.push({
            fileId: uploaded.id,
            fileName: file.name,
            fileUrl: uploaded.url,
            viewToken: uploaded.viewToken,
          })
        } catch {
          toast.error(`Failed to upload ${file.name}`)
        }
      }

      onFilesChange(newFiles)
      setIsUploading(false)
    },
    [uploadedFiles, onFilesChange],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.pdf',
    multiple: true,
    disabled: isUploading,
  })

  const removeFile = (fileId: number) => {
    onFilesChange(uploadedFiles.filter(f => f.fileId !== fileId))
  }

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 6,
          textAlign: 'center',
          cursor: isUploading ? 'wait' : 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s',
          '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
        }}
      >
        <input {...getInputProps()} />
        {isUploading ? (
          <>
            <CircularProgress size={48} />
            <Typography variant="h6" color="text.secondary" mt={1}>
              Uploading files...
            </Typography>
          </>
        ) : (
          <>
            <MdCloudUpload size={48} color="#9e9e9e" />
            <Typography variant="h6" color="text.secondary" mt={1}>
              {isDragActive ? 'Drop certificate PDFs here...' : 'Drag & drop certificate PDFs here'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse files
            </Typography>
          </>
        )}
      </Box>

      {uploadedFiles.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} ready for AI extraction
          </Typography>
          <List dense>
            {uploadedFiles.map(file => (
              <ListItem
                key={file.fileId}
                secondaryAction={
                  <IconButton edge="end" onClick={() => removeFile(file.fileId)} size="small">
                    <MdClose />
                  </IconButton>
                }
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <MdPictureAsPdf size={24} color="#e53935" />
                </ListItemIcon>
                <ListItemText primary={file.fileName} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  )
}
